<script lang="ts">
import InstantWinPageSkeleton from '@/components/instant-wins/InstantWinPageSkeleton.vue'
import { useInstantWinsStore } from '@/stores/instant-wins'
import type {
  GameSessionPageData,
  InstantPackDraw,
  InstantSpinDraw,
  InstantWinDraw,
  InstantWinWinnerEntries,
} from '@/types'
import { isInFuture, isInPast } from '@/utils/date-time-utils'
import {
  InstantWinType,
} from '@/utils/helper-objects'
import { type Component, type PropType, defineComponent, inject, provide, ref } from 'vue'
import { InstantGameError } from '../domain/InstantGameError'

import { InstantGameCheckoutComponentKey, InstantGameComponentsKey, InstantGameSessionCurrentDataKey, InstantGameSessionServiceKey } from '../injection-keys'

/**
 * The "Instant Games" view
 */
export default defineComponent({
  components: {
    InstantWinPageSkeleton,
  },
  provide() {
    return {
      [InstantGameCheckoutComponentKey]: this.checkoutComponent,
      results: this.resultsComponent,
    }
  },
  props: {
    gameId: {
      type: String,
      required: true,
    },
    sessionId: {
      type: String,
    },
    checkoutComponent: {
      type: Object as PropType<Component>,
    },
    resultsComponent: {
      type: Object as PropType<Component>,
    },
  },
  emits: ['error', 'game-load', 'session-load', 'complete', 'win', 'lose'],
  setup() {
    const instantWinsStore = useInstantWinsStore()
    const GameComponents = inject(InstantGameComponentsKey) ?? {}
    const gameSessionService = inject(InstantGameSessionServiceKey)
    /** 
     * A `ref` containing the current session data, which will be passed down to
     * components via Vue's `provide` API. This value is then consumed using the 
     * `useGameSession()` composable.
     */
    const providerSessionData = ref<GameSessionPageData|null>(null)
    provide(InstantGameSessionCurrentDataKey,  providerSessionData)
    return {
      InstantWinType,
      instantWinsStore,
      GameComponents,
      gameSessionService,
      providerSessionData
    }
  },
  data() {
    return {
      resolvedInstantWin: null as
        | InstantWinDraw
        | InstantSpinDraw
        | InstantPackDraw
        | null,
      isLoadingInstantWin: false,
      isLoadingGameSession: false,
      gameSessionData: undefined as GameSessionPageData | undefined,
      gameOver: false,
    }
  },
  computed: {
    params(): any {
      return {
        code: this.gameId,
        session: this.sessionId,
      }
    },
    isDemo(): boolean {
      if (!this.gameSessionService) {
        return false;
      }
      return (
        this.gameSessionData?.game_session.game_session_id ===
        this.gameSessionService.demoSessionIdentifier
      )
    },
  },
  watch: {
    /**
     * Vue watcher handler that reacts to changes in the route params (`code`,
     * `session`)to load and manage the current Instant Win draw and its
     * associated game session.
     *
     * - When the `code` changes, it resolves the corresponding Instant Win
     *   draw, updates loading state, sets the page title, and triggers
     *   analytics tracking.
     * - If no valid draw is found, it clears any existing game session data.
     * - If the route is in demo mode, it verifies support and creates a demo
     *   session.
     * - Otherwise, it ensures the correct game session is loaded for the given
     *   `session` id, updating loading state as needed.
     *
     * This handler runs immediately on component initialization.
     *
     * @param params - Object containing:
     * @param params.code - The Instant Win draw identifier from the route.
     * @param params.session - The game session identifier from the route.
     * @returns A Promise that resolves once the relevant draw and session
     *          state are updated.
     */
    params: {
      async handler({ code, session }) {
        console.log('PARAMS CHANGED', { code, session })
        if (this.resolvedInstantWin?.code !== code) {
          this.isLoadingInstantWin = true
          let instantWin = await this.resolveInstantWin(code)
          this.isLoadingInstantWin = false
          if (instantWin) {
            // If the game is valid but we can't render it, emit an error and
            // `null` the instantWin so the view fails safe.
            if (!this.GameComponents[instantWin.instant_win_type_id]) {
              this.$emit('error', new InstantGameError('This game type is unsupported'))
              instantWin = null;
            } else {
              this.$emit('game-load', instantWin)
            }
          } else {
            this.$emit('error', new InstantGameError('This game is currently unavailable'))
          }
          this.resolvedInstantWin = instantWin
        }

        // If there's no instant win, or if no session ID was provided, clear
        // the current game session data.
        if (!this.resolvedInstantWin || !session) {
          this.gameSessionData = undefined
          this.gameOver = false
          return
        }

        // If there's no configured game session service, we can't start or
        // resume a game. Add a warning to the console and bail out.
        if (!this.gameSessionService) {
          console.warn('No InstantGameSessionService found. Did you add one to app.use()?')
          return
        }

        // If the provided session ID is for a demo session, create a new demo
        // session and set it as active.
        // BUG `this.gameSessionId` should be `session`?
        if (session === this.gameSessionService.demoSessionIdentifier) {
          if (!this.gameSessionService.supportsDemoSession(this.resolvedInstantWin)) {
            this.gameSessionData = undefined
            return
          }
          this.gameSessionData = await this.gameSessionService.createDemoSession(
            this.resolvedInstantWin
          )

          this.providerSessionData = this.gameSessionData
          return
        }
  

        // 
        if (!this.gameSessionData?.game_session.game_session_id !== session) {
          this.isLoadingGameSession = true
          this.gameSessionData = await this.resolveGameSession(session)
          if (this.gameSessionData) {
            this.$emit('session-load', this.gameSessionData)
          }
          this.providerSessionData = this.gameSessionData ?? null
          this.isLoadingGameSession = false
          if (this.gameSessionData?.game_session.remaining_plays === 0) {
            this.gameOver = true
          } else {
            this.gameOver = false
          }
        }
      },
      immediate: true,
    },

    // When store refetches (from broadcast), refetch current individual game
    // This handles hidden games (is_visible=false) which aren't in list endpoint
    async 'instantWinsStore.lastFetchedAt'() {
      this.resolvedInstantWin = await this.resolveInstantWin(this.gameId)
    },
  },
  methods: {
    handleWin(entries: InstantWinWinnerEntries) {
      if (!this.isDemo) {
        this.$emit('win', entries)
      }
    },
    async handleComplete() {
      if (this.isDemo) {
        this.$emit('complete')
        return
      } 
      this.gameOver = true
      if (this.sessionId) {
        this.gameSessionData = await this.resolveGameSession(this.sessionId)
      }
    },
    /**
     * Attempts to resolve and validate an Instant Win draw by its code.
     *
     * Fetches the Instant Win data from the store and ensures the draw is
     * currently active. If the draw has not yet opened or has already closed,
     * an error message is displayed and `null` is returned to prevent
     * rendering. If the fetch fails, a generic error message is shown.
     *
     * @param code - The unique identifier for the Instant Win draw.
     * @returns A Promise resolving to the valid InstantWinDraw if available and
     *          active, or `null` if the code is invalid, the draw is not
     *          active, or an error occurs.
     */
    async resolveInstantWin(code: string): Promise<InstantWinDraw | null> {
      if (code) {
        try {
          const instantWin = await this.instantWinsStore.getInstantWin(code)

          // If the draw is in the future, show an error and return `null` to
          // prevent the game from rendering.
          if (instantWin && isInFuture(instantWin.main_prize.open_at)) {
            this.$emit('error', new InstantGameError('This Instant Prize is not open yet, please come back after: ' +
                instantWin.main_prize.open_at.toFormat('d/MM/y')
            ))
            return null
          }

          // If the draw has already happened, show an error and return `null`
          // to prevent the game from rendering.
          if (instantWin && isInPast(instantWin.main_prize.close_at)) {
            this.$emit('error', 
              new InstantGameError('This Instant Prize is in the past, please try a different Instant Prize Draw')
            )
            return null
          }

          return instantWin
        } catch {
          this.$emit('error', new InstantGameError('Unable to load Instant Win data. Please try again later.'))
  
        }
      }
      return null
    },

    /**
     * Attempts to resolve and validate a Game Session by its ID.
     *
     * Fetches the session from the GameSession service. If the fetch fails, a
     * generic error message is shown.
     *
     * @param session - The unique identifier for the game session.
     * @returns A Promise resolving to the valid GameSession if available, or
     *          `undefined` if the session is invalid, or no longer exists.
     */

    async resolveGameSession(
      session: string
    ): Promise<GameSessionPageData | undefined> {
      if (!session || !this.gameSessionService) {
        return undefined
      }
        
      try {
        return await this.gameSessionService.getGameSessionById(session)
      } catch {
        this.$emit('error', new InstantGameError('Unable to load game session.'))
      }

      return undefined
    },
  },
})
</script>

<template>
  <InstantWinPageSkeleton v-if="isLoadingInstantWin" />
  <template v-else-if="resolvedInstantWin && !isLoadingGameSession">
    <!-- If the game session is complete, show the results -->
    <component
      :is="resultsComponent"
      v-if="resultsComponent && gameOver"
      :instant-win="resolvedInstantWin"
      :game-session-data
      :prizes="gameSessionData?.played_entries?.winning_entries ?? []"
    />

    <!-- If we get here, show the game -->
    <component
      :is="GameComponents[resolvedInstantWin.instant_win_type_id]"
      v-else
      :instant-win="resolvedInstantWin"
      :game-session-data
      @win="handleWin"
      @lose="$emit('lose', $event)"
      @complete="handleComplete"
    >
    </component>
  </template>
</template>
